// Global Instructions Rule Applied!

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  FundTransactionRecord,
  validateFundTransactionRecord
} from '../../../state_manager/data_models/fund_transaction_record.ts'
import {
  FundTransactionDetail,
  FundTransactionSavePayload,
  validateFundTransactionDetail
} from '../../../state_manager/data_models/fund_transaction_detail.ts'
import {
  FeeBookingPosition,
  validateFeeBookingPosition
} from '../../../state_manager/data_models/fee_booking_position.ts'
import {
  FundClosingCandidates,
  validateFundClosingCandidates
} from '../../../state_manager/data_models/fund_closing_candidate.ts'
import {
  FundClosingEarlierYears,
  validateFundClosingEarlierYears
} from '../../../state_manager/data_models/fund_closing_earlier_years.ts'
import {
  FundTransferCandidates,
  validateFundTransferCandidates
} from '../../../state_manager/data_models/fund_transfer_candidate.ts'
import {
  FundTransferPreview,
  validateFundTransferPreview
} from '../../../state_manager/data_models/fund_transfer_preview.ts'
import {
  FeeBasis,
  FeeHistoryRow,
  validateFeeBasis,
  validateFeeHistory
} from '../../../state_manager/data_models/fee_basis.ts'
import { FilterValue, SortConfig } from '../../global_configs/tableViewPreferences.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface FundTransactionsPaginatedResponse {
  data: FundTransactionRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface FundTransactionDetailResponse {
  data: FundTransactionDetail | null
  error?: string
}

export interface FundTransactionActionResponse {
  data: Record<string, any> | null
  error?: string
}

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

export const fetchFundTransactionsFiltered = async (
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null,
  options?: { fundId?: string | null }
): Promise<FundTransactionsPaginatedResponse> => {
  try {
    const requestBody = {
      data: {
        page,
        page_size: pageSize ?? 200,
        sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
        filters: filters.map((f) => ({ key: f.key, value: f.value })),
        ...(options?.fundId ? { fund_id: options.fundId } : {})
      }
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_TRANSACTIONS.FILTER), {
      method: API_ROUTES.FUND_TRANSACTIONS.FILTER.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch fund transactions: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rows = Array.isArray(json.data) ? json.data : []
    return {
      data: validateFundTransactionRecord(rows, true),
      hasMore: Boolean(json.has_more),
      totalCount: json.count ?? rows.length
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fund transactions:', error)
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const fetchFundTransactionsFilterOptions = async (
  accessToken: string | null = null
): Promise<{ data: Record<string, Array<{ value: string; label: string }>>; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_TRANSACTIONS.FILTER_OPTIONS), {
      method: API_ROUTES.FUND_TRANSACTIONS.FILTER_OPTIONS.method,
      headers: authHeaders(accessToken)
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch filter options: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: Record<string, Array<{ value: string; label: string }>> }
    return { data: json.data ?? {} }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fund transaction filter options:', error)
    return { data: {}, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundTransactionDetail = async (
  transactionId: string,
  accessToken: string | null = null
): Promise<FundTransactionDetailResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_TRANSACTIONS.GET, { id: transactionId }, {})
    const response = await fetch(url, { method: API_ROUTES.FUND_TRANSACTIONS.GET.method, headers: authHeaders(accessToken) })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      if (response.status === 404) throw new Error('Fund transaction not found')
      throw new Error(`Failed to fetch fund transaction: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (!json.data) return { data: null }
    return { data: validateFundTransactionDetail(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fund transaction detail:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/**
 * Create or update a fund transaction. The backend regenerates the journal
 * preview and returns the full refreshed detail in the same round-trip.
 */
export const saveFundTransaction = async (
  payload: FundTransactionSavePayload,
  accessToken: string | null = null
): Promise<FundTransactionDetailResponse> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_TRANSACTIONS.SAVE), {
      method: API_ROUTES.FUND_TRANSACTIONS.SAVE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })

    const json = (await response.json().catch(() => ({}))) as { data?: unknown; message?: string; error?: string }

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(json.message || json.error || `Failed to save fund transaction: ${response.status}`)
    }

    if (!json.data) return { data: null }
    return { data: validateFundTransactionDetail(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error saving fund transaction:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

async function postTransactionAction(
  route: typeof API_ROUTES.FUND_TRANSACTIONS.BOOK | typeof API_ROUTES.FUND_TRANSACTIONS.UNBOOK,
  transactionId: string,
  accessToken: string | null
): Promise<FundTransactionActionResponse> {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, route, { id: transactionId }, {})
    const response = await fetch(url, { method: route.method, headers: authHeaders(accessToken) })
    const json = (await response.json().catch(() => ({}))) as {
      data?: Record<string, any>
      message?: string
      error?: string
    }

    if (!response.ok) {
      // 422 carries the unbalanced-totals payload in data - surface both.
      const message = json.error || json.message || `Request failed: ${response.status}`
      return { data: json.data ?? null, error: message }
    }

    return { data: json.data ?? null }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const bookFundTransaction = (transactionId: string, accessToken: string | null = null) =>
  postTransactionAction(API_ROUTES.FUND_TRANSACTIONS.BOOK, transactionId, accessToken)

export const unbookFundTransaction = (transactionId: string, accessToken: string | null = null) =>
  postTransactionAction(API_ROUTES.FUND_TRANSACTIONS.UNBOOK, transactionId, accessToken)

export const trashFundTransaction = async (
  transactionId: string,
  accessToken: string | null = null
): Promise<FundTransactionActionResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_TRANSACTIONS.DELETE, { id: transactionId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUND_TRANSACTIONS.DELETE.method,
      headers: authHeaders(accessToken)
    })
    const json = (await response.json().catch(() => ({}))) as {
      data?: Record<string, any>
      message?: string
      error?: string
    }

    if (!response.ok) {
      return { data: json.data ?? null, error: json.error || json.message || `Request failed: ${response.status}` }
    }
    return { data: json.data ?? null }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** The Closing form's Earlier years read (view__fund_closing_earlier_years):
 *  the engine's carry-forward per partner and tax category at the closing
 *  date; a Draft is previewed with its selection applied, nothing written. */
export const fetchFundClosingEarlierYears = async (
  transactionId: string,
  accessToken: string | null = null
): Promise<{ data: FundClosingEarlierYears | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_TRANSACTIONS.EARLIER_YEARS, { id: transactionId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.FUND_TRANSACTIONS.EARLIER_YEARS.method,
      headers: authHeaders(accessToken)
    })
    const json = (await response.json().catch(() => ({}))) as { data?: unknown; error?: string; message?: string }
    if (!response.ok) {
      return { data: null, error: json.error || json.message || `Failed to fetch the earlier years: ${response.status}` }
    }
    return { data: validateFundClosingEarlierYears(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching the earlier years:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** Commitments a Closing transaction can close, with selection state for `transactionId` (null = new). */
export const fetchFundClosingCandidates = async (
  fundId: string,
  transactionId: string | null,
  accessToken: string | null = null
): Promise<{ data: FundClosingCandidates | null; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.FUND_TRANSACTIONS.CLOSING_CANDIDATES,
      { fundId },
      transactionId ? { transaction_id: transactionId } : {}
    )
    const response = await fetch(url, {
      method: API_ROUTES.FUND_TRANSACTIONS.CLOSING_CANDIDATES.method,
      headers: authHeaders(accessToken)
    })
    const json = (await response.json().catch(() => ({}))) as { data?: unknown; error?: string; message?: string }
    if (!response.ok) {
      return { data: null, error: json.error || json.message || `Failed to fetch closing candidates: ${response.status}` }
    }
    return { data: validateFundClosingCandidates(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching closing candidates:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** Commitments a Transfer of Interest can assign, with selection state for `transactionId` (null = new). */
export const fetchFundTransferCandidates = async (
  fundId: string,
  transactionId: string | null,
  accessToken: string | null = null
): Promise<{ data: FundTransferCandidates | null; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.FUND_TRANSACTIONS.TRANSFER_CANDIDATES,
      { fundId },
      transactionId ? { transaction_id: transactionId } : {}
    )
    const response = await fetch(url, {
      method: API_ROUTES.FUND_TRANSACTIONS.TRANSFER_CANDIDATES.method,
      headers: authHeaders(accessToken)
    })
    const json = (await response.json().catch(() => ({}))) as { data?: unknown; error?: string; message?: string }
    if (!response.ok) {
      return { data: null, error: json.error || json.message || `Failed to fetch transfer candidates: ${response.status}` }
    }
    return { data: validateFundTransferCandidates(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching transfer candidates:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** A commitment's position as at a transfer date (what a Transfer of Interest hands over). */
export const fetchFundTransferPreview = async (
  fundId: string,
  fundCommitmentId: string,
  date: string,
  accessToken: string | null = null
): Promise<{ data: FundTransferPreview | null; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.FUND_TRANSACTIONS.TRANSFER_PREVIEW,
      { fundId },
      { fund_commitment_id: fundCommitmentId, date }
    )
    const response = await fetch(url, {
      method: API_ROUTES.FUND_TRANSACTIONS.TRANSFER_PREVIEW.method,
      headers: authHeaders(accessToken)
    })
    const json = (await response.json().catch(() => ({}))) as { data?: unknown; error?: string; message?: string }
    if (!response.ok) {
      return { data: null, error: json.error || json.message || `Failed to fetch transfer preview: ${response.status}` }
    }
    return { data: validateFundTransferPreview(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching transfer preview:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

// chargeDate: the date the fees are charged (the fee transaction's date) -
// due and booked come back on whoever holds each interest on it.
export const fetchFeeBookingPosition = async (
  fundId: string,
  splitDate: string,
  periodTo: string,
  chargeDate: string,
  accessToken: string | null = null
): Promise<{ data: FeeBookingPosition | null; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.FUND_TRANSACTIONS.FEE_POSITION,
      { fundId },
      { split_date: splitDate, period_to: periodTo, charge_date: chargeDate }
    )
    const response = await fetch(url, {
      method: API_ROUTES.FUND_TRANSACTIONS.FEE_POSITION.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch fee booking position: ${response.status}`)
    const json = (await response.json()) as { data?: unknown }
    return { data: validateFeeBookingPosition(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fee booking position:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFeeFundBasis = async (
  fundId: string,
  periodFrom: string,
  periodTo: string,
  accessToken: string | null = null
): Promise<{ data: FeeBasis | null; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.FUND_TRANSACTIONS.FEE_BASIS,
      { fundId },
      { period_from: periodFrom, period_to: periodTo }
    )
    const response = await fetch(url, {
      method: API_ROUTES.FUND_TRANSACTIONS.FEE_BASIS.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch fee basis: ${response.status}`)
    const json = (await response.json()) as { data?: unknown }
    return { data: validateFeeBasis(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fee basis:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFeeBookingHistory = async (
  fundId: string,
  accessToken: string | null = null
): Promise<{ data: FeeHistoryRow[] | null; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUND_TRANSACTIONS.FEE_HISTORY, { fundId })
    const response = await fetch(url, {
      method: API_ROUTES.FUND_TRANSACTIONS.FEE_HISTORY.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch fee history: ${response.status}`)
    const json = (await response.json()) as { data?: unknown }
    return { data: validateFeeHistory(json.data ?? []) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fee history:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}
